'use client';

import { Control, FieldErrors } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { Button, Input } from '@heroui/react';
import { FaUser, FaEnvelope, FaPhone, FaArrowRight } from 'react-icons/fa6';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { ContactFormData, useScheduler } from '../SchedulerContext';
import {
  schedulerDefaultFirstNameRules,
  schedulerDefaultLastNameRules,
} from '../utils/schedulerContactNameFieldRules';
import { validateSingleEmailAddress } from '@/util/functions/validation/isSingleEmailAddress';

type ContactFormFieldsProps = {
  control: Control<ContactFormData>;
  errors: FieldErrors<ContactFormData>;
  onSubmit?: (e?: React.BaseSyntheticEvent) => Promise<void> | void;
  onCancel?: () => void;
  cancelLabel?: string;
  submitLabel?: string;
  showCancel?: boolean;
  className?: string;
  isLoading?: boolean; // Add loading state
  isValid?: boolean; // Form validation state
  isReadOnly?: boolean; // ATT-194: Disable editing when contact is found
  // Optional custom validation rules
  firstNameRules?: Record<string, any>;
  lastNameRules?: Record<string, any>;
  emailRules?: Record<string, any>;
  phoneRules?: Record<string, any>;
};

export default function ContactFormFields({
  control,
  errors,
  onSubmit,
  onCancel,
  cancelLabel = 'Back',
  submitLabel = 'Continue',
  showCancel = true,
  className = '',
  isLoading = false,
  isValid,
  isReadOnly = false,
  firstNameRules = schedulerDefaultFirstNameRules,
  lastNameRules = schedulerDefaultLastNameRules,
  emailRules = {
    validate: (value: string) =>
      validateSingleEmailAddress(value, { required: true }),
  },
  phoneRules = {
    required: 'Phone number is required',
    validate: (value: string) => {
      if (!value || !value.trim()) {
        return 'Phone number is required';
      }
      try {
        if (!isValidPhoneNumber(value, 'US')) {
          return 'Please enter a valid phone number';
        }
        return true;
      } catch {
        return 'Please enter a valid phone number';
      }
    },
  },
}: ContactFormFieldsProps) {
  const { companyStyles } = useScheduler();

  // When read-only (existing contact found), don't require phone — they can't edit it and may have none
  const effectivePhoneRules = isReadOnly ? {} : phoneRules;

  // Check if form has validation errors
  const hasErrors = Object.keys(errors).length > 0;
  // Disable button if form is invalid (explicitly false) or has errors
  const isFormInvalid = isValid === false || hasErrors;

  return (
    <form noValidate onSubmit={onSubmit} className={`space-y-2 ${className}`}>
      <div className='grid gap-2 sm:grid-cols-2'>
        <Controller
          name='firstName'
          control={control}
          rules={firstNameRules}
          render={({ field }) => (
            <Input
              {...field}
              type='text'
              label='First Name'
              placeholder='John'
              startContent={<FaUser className='text-gray-400' />}
              variant='bordered'
              isRequired
              isInvalid={!!errors.firstName}
              errorMessage={errors.firstName?.message}
              isDisabled={isReadOnly}
              classNames={{
                inputWrapper: 'border bg-white shadow-none',
                label: 'font-light uppercase text-xs',
                input: 'text-base md:text-sm',
              }}
            />
          )}
        />

        <Controller
          name='lastName'
          control={control}
          rules={lastNameRules}
          render={({ field }) => (
            <Input
              {...field}
              type='text'
              label='Last Name'
              placeholder='Doe'
              startContent={<FaUser className='text-gray-400' />}
              variant='bordered'
              isRequired
              isInvalid={!!errors.lastName}
              errorMessage={errors.lastName?.message}
              isDisabled={isReadOnly}
              classNames={{
                inputWrapper: 'border bg-white shadow-none',
                label: 'font-light uppercase text-xs',
                input: 'text-base md:text-sm',
              }}
            />
          )}
        />
      </div>

      <Controller
        name='email'
        control={control}
        rules={emailRules}
        render={({ field }) => (
          <Input
            {...field}
            type='email'
            label='Email Address'
            placeholder='your.email@example.com'
            startContent={<FaEnvelope className='text-gray-400' />}
            variant='bordered'
            isRequired
            isInvalid={!!errors.email}
            errorMessage={errors.email?.message}
            isDisabled={isReadOnly}
            classNames={{
              inputWrapper: 'border bg-white shadow-none',
              label: 'font-light uppercase text-xs',
              input: 'text-base md:text-sm',
            }}
          />
        )}
      />

      <Controller
        name='phone'
        control={control}
        rules={effectivePhoneRules}
        render={({ field }) => (
          <Input
            {...field}
            type='tel'
            label='Phone Number'
            placeholder='(555) 123-4567'
            startContent={<FaPhone className='text-gray-400' />}
            variant='bordered'
            isRequired={!isReadOnly}
            isInvalid={!!errors.phone}
            errorMessage={errors.phone?.message}
            isDisabled={isReadOnly}
            classNames={{
              inputWrapper: 'border bg-white shadow-none',
              label: 'font-light uppercase text-xs',
              input: 'text-base md:text-sm',
            }}
          />
        )}
      />

      <div className='flex gap-3 pt-4'>
        {showCancel && onCancel && (
          <Button
            type='button'
            variant='flat'
            color='default'
            onPress={onCancel}
            className='flex-1'
          >
            {cancelLabel}
          </Button>
        )}
        <Button
          type='submit'
          className={`flex-1 ${companyStyles.secondaryButtonClassName}`}
          style={companyStyles.secondaryButtonStyles}
          endContent={submitLabel === 'Continue' ? <FaArrowRight /> : undefined}
          isLoading={isLoading}
          isDisabled={isLoading || isFormInvalid}
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
